import { useEffect, useState, useMemo } from "react";
import type { FormField } from "../../components/AdminDataModal";
import type { Column } from "../../components/DataTable";
import { useMessageModal } from "../../contexts/MessageModalContext";
import { useFormat } from "../../../context/FormatContext";
import DataTable from "../../components/DataTable";
import AdminListToolkit from "../../components/listing/AdminListToolkit";
import AdminDataModal from "../../components/AdminDataModal";

interface RegionOption {
  id: number;
  region_name: string;
}

export default function PublicEmails() {
  const BASE = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const { showModal } = useMessageModal();
  const { formatDateToDMY } = useFormat();

  const [data, setData] = useState<any[]>([]);
  const [regions, setRegions] = useState<RegionOption[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  const fetchEmails = async () => {
    try {
      const res = await fetch(`${BASE}/admin/email-settings/public-emails`, {
        credentials: "include",
      });

      const result = await res.json();
      const freshData = result.data || [];

      if (result.success) {
        setData(freshData);
        setSelectedRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );

        setActiveRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );
      }
    } catch (error) {
      console.error("Fetch Public Emails Error:", error);
    }
  };

  const fetchRegions = async () => {
    try {
      const res = await fetch(
        `${BASE}/admin/email-settings/regions-for-campaign`,
        {
          credentials: "include",
        },
      );

      const result = await res.json();

      if (result.success) {
        setRegions(result.data || []);
      }
    } catch (error) {
      console.error("Fetch Regions Error:", error);
    }
  };

  useEffect(() => {
    fetchEmails();
    fetchRegions();
  }, []);

  const handleSubmit = async (formData: any) => {
    try {
      const method = modalMode === "add" ? "POST" : "PUT";

      const url =
        modalMode === "add"
          ? `${BASE}/admin/email-settings/public-emails`
          : `${BASE}/admin/email-settings/public-emails/${activeRow.id}`;

      const payload = {
        email: formData.email,
        region_id: formData.region_id ? Number(formData.region_id) : null,
        status: formData.status || "active",
      };

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (result.success) {
        fetchEmails();
        setModalOpen(false);
        setActiveRow(null);

        showModal({
          type: "success",
          title: "Success",
          message:
            modalMode === "add"
              ? "Public email added successfully."
              : "Public email updated successfully.",
        });
      }
    } catch (error) {
      console.error("Submit Public Email Error:", error);
    }
  };

  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete Email",
      message: "Are you sure you want to delete this email?",
      confirmText: "Delete",
      onConfirm: async () => {
        await fetch(`${BASE}/admin/email-settings/public-emails/${row.id}`, {
          method: "DELETE",
          credentials: "include",
        });

        fetchEmails();
        setSelectedRow(null);

        showModal({
          type: "success",
          title: "Deleted",
          message: "Public email deleted successfully.",
        });
      },
    });
  };

  const normalizeRow = (row: any) => ({
    ...row,
    region_id:
      row?.region_id !== null && row?.region_id !== undefined
        ? String(row.region_id)
        : "",
    status: row?.status || "active",
  });

  const emailFields: FormField[] = useMemo(
    () => [
      { key: "id", label: "ID", colSpan: 2 },
      { key: "email", label: "Email Address", colSpan: 2 },
      {
        key: "region_id",
        label: "Region",
        type: "select",
        options: regions.map((region) => ({
          value: String(region.id),
          label: region.region_name,
        })),
        colSpan: 2,
      },
      {
        key: "status",
        label: "Status",
        type: "select",
        options: [
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
        ],
        colSpan: 2,
      },
    ],
    [regions],
  );

  const columns: Column[] = [
    { key: "id", label: "ID" },
    { key: "email", label: "Email" },
    {
      key: "region_name",
      label: "Region",
      render: (row: any) => row.region_name || "-",
    },
    {
      key: "status",
      label: "Status",
      render: (row: any) => (
        <span
          className={row.status === "active" ? "badge-success" : "badge-danger"}
        >
          {row.status?.charAt(0).toUpperCase() + row.status?.slice(1)}
        </span>
      ),
    },
    {
      key: "created_at",
      label: "Created At",
      render: (row: any) => formatDateToDMY(row.created_at),
    },
  ];

  return (
    <div>
      <AdminListToolkit
        title="Public Emails"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => {
          setModalMode("add");
          setModalOpen(true);
          setActiveRow({
            email: "",
            region_id: "",
            status: "active",
          });
        }}
        onView={(row) => {
          setModalMode("view");
          setActiveRow(normalizeRow(row));
          setModalOpen(true);
        }}
        onEdit={(row) => {
          setModalMode("edit");
          setActiveRow(normalizeRow(row));
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport={false}
      >
        {(filteredData) => (
          <DataTable
            columns={columns}
            data={filteredData}
            selectedRowId={selectedRow?.id}
            onRowSelect={setSelectedRow}
          />
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Public Email"
        fields={emailFields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
